import { useEffect, useRef, useCallback } from 'react';

/**
 * Accessibility utilities and hooks
 */

/**
 * Hook to trap focus within an element (for modals)
 * @param {boolean} isActive - Whether focus trap is active
 * @param {React.RefObject} containerRef - Ref to the container element
 */
export function useFocusTrap(isActive, containerRef) {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const focusableElements = container.querySelectorAll(focusableSelectors);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Store previously focused element to restore later
    const previouslyFocused = document.activeElement;

    // Focus first element
    if (firstElement) {
      firstElement.focus();
    }

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Restore focus when trap is deactivated
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [isActive, containerRef]);
}

/**
 * Hook to handle Escape key to close modals/dialogs
 * @param {boolean} isActive - Whether the handler is active
 * @param {function} onEscape - Callback when Escape is pressed
 */
export function useEscapeKey(isActive, onEscape) {
  useEffect(() => {
    if (!isActive) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscape();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, onEscape]);
}

/**
 * Hook to announce messages to screen readers
 * Returns a function to announce messages
 */
export function useAnnounce() {
  const announceRef = useRef(null);

  useEffect(() => {
    // Create or get the live region
    let liveRegion = document.getElementById('sr-announcer');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'sr-announcer';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }
    
    announceRef.current = liveRegion;

    return () => {
      // Don't remove on unmount - other components may need it
    };
  }, []);

  const announce = useCallback((message, priority = 'polite') => {
    if (!announceRef.current) return;
    
    // Update aria-live based on priority
    announceRef.current.setAttribute('aria-live', priority);
    
    // Clear and set message (ensures announcement even if same message)
    announceRef.current.textContent = '';
    requestAnimationFrame(() => {
      if (announceRef.current) {
        announceRef.current.textContent = message;
      }
    });
  }, []);

  return announce;
}

/**
 * Hook to manage focus on mount
 * @param {React.RefObject} ref - Ref to element to focus
 * @param {boolean} shouldFocus - Whether to focus
 */
export function useFocusOnMount(ref, shouldFocus = true) {
  useEffect(() => {
    if (shouldFocus && ref.current) {
      // Small delay to ensure element is fully rendered
      requestAnimationFrame(() => {
        ref.current?.focus();
      });
    }
  }, [shouldFocus, ref]);
}

/**
 * Generate unique IDs for accessibility associations
 */
let idCounter = 0;
export function generateId(prefix = 'a11y') {
  return `${prefix}-${++idCounter}`;
}

/**
 * Visually hidden but accessible to screen readers
 * Use as className="sr-only"
 */
export const srOnlyStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: '0',
};

/**
 * Component for visually hidden text (screen reader only)
 */
export function VisuallyHidden({ children, as: Component = 'span' }) {
  return (
    <Component style={srOnlyStyles}>
      {children}
    </Component>
  );
}

/**
 * Skip link for keyboard navigation
 */
export function SkipLink({ href = '#main-content', children = 'Skip to main content' }) {
  return (
    <a
      href={href}
      className="skip-link"
      style={{
        position: 'absolute',
        top: '-40px',
        left: '0',
        padding: '8px 16px',
        backgroundColor: 'var(--color-brand-primary)',
        color: 'white',
        textDecoration: 'none',
        zIndex: '9999',
        transition: 'top 0.2s',
      }}
      onFocus={(e) => {
        e.target.style.top = '0';
      }}
      onBlur={(e) => {
        e.target.style.top = '-40px';
      }}
    >
      {children}
    </a>
  );
}
